/**
 * Shared "letter" chrome for all email/password auth screens.
 *
 * The visual language mirrors the landing page — a paper-stock card
 * floating over the warm-dark background, red margin rule on the left,
 * subtle ledger lines behind. Every auth screen (login/signup/forgot/reset)
 * composes this so brand feel is identical.
 */
import { Link } from "react-router-dom";

export const AuthShell = ({ eyebrow, title, subtitle, footer, children }) => {
  return (
    <div className="min-h-screen relative overflow-hidden bg-[#0A0806] text-[#F4EBDC]">
      {/* radial warmth */}
      <div
        className="pointer-events-none absolute inset-0"
        style={{
          background:
            "radial-gradient(900px 500px at 12% 8%, rgba(232,185,108,0.10), transparent 60%), radial-gradient(700px 500px at 100% 100%, rgba(140,30,30,0.16), transparent 55%)",
        }}
      />
      {/* ledger */}
      <div
        className="pointer-events-none absolute inset-0 opacity-[0.5]"
        style={{
          backgroundImage:
            "repeating-linear-gradient(to bottom, transparent 0 76px, rgba(244,235,220,0.05) 76px 77px)",
        }}
      />
      {/* red margin rule */}
      <div className="pointer-events-none absolute top-0 bottom-0 left-[88px] w-px bg-[#B43C3C]/40 hidden md:block" />

      <div className="relative z-10 min-h-screen flex items-center justify-center px-6 py-12">
        <div className="w-full max-w-md">
          {/* brand */}
          <Link
            to="/"
            data-testid="auth-brand-link"
            className="inline-flex items-center gap-2 mb-10 group"
          >
            <span
              className="font-serif text-3xl tracking-tight text-[#F4EBDC] group-hover:text-[#E8B96C] transition-colors"
              style={{ fontFamily: "'Fraunces', 'Cormorant Garamond', serif" }}
            >
              Quill<span className="text-[#E8B96C]">.</span>
            </span>
          </Link>

          {/* card */}
          <div
            className="relative border border-[#E8B96C]/15 rounded-[6px] p-8 md:p-10 backdrop-blur-[8px]"
            style={{
              background:
                "linear-gradient(180deg, rgba(30,22,14,0.75) 0%, rgba(20,14,10,0.85) 100%)",
              boxShadow:
                "0 24px 60px -20px rgba(0,0,0,0.6), inset 0 1px 0 rgba(244,235,220,0.04)",
            }}
          >
            {eyebrow && (
              <div className="text-[10px] uppercase tracking-[0.24em] text-[#E8B96C] mb-3">
                {eyebrow}
              </div>
            )}
            <h1
              className="text-3xl md:text-4xl font-medium leading-tight tracking-tight mb-2 text-[#F4EBDC]"
              style={{ fontFamily: "'Fraunces', 'Cormorant Garamond', serif" }}
            >
              {title}
            </h1>
            {subtitle && (
              <p className="text-sm text-[#C9BEA9] leading-relaxed mb-8">
                {subtitle}
              </p>
            )}
            {children}
          </div>

          {footer && (
            <div className="mt-8 text-center text-sm text-[#A89680]">{footer}</div>
          )}
        </div>
      </div>
    </div>
  );
};

export const AuthField = ({
  label,
  type = "text",
  value,
  onChange,
  placeholder,
  autoComplete,
  required = true,
  testId,
  disabled = false,
  hint,
  rightSlot,
}) => (
  <label className="block">
    <div className="flex items-center justify-between mb-2">
      <span className="text-[11px] uppercase tracking-[0.22em] text-[#A89680]">
        {label}
      </span>
      {rightSlot}
    </div>
    <input
      type={type}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      autoComplete={autoComplete}
      required={required}
      disabled={disabled}
      data-testid={testId}
      className="w-full px-4 py-3 bg-[#0F0B08] border border-[#E8B96C]/15 rounded-[4px] text-[#F4EBDC] placeholder-[#5C4F3E] focus:outline-none focus:border-[#E8B96C]/60 focus:ring-1 focus:ring-[#E8B96C]/30 transition-all text-[15px]"
    />
    {hint && <div className="mt-1.5 text-[11px] text-[#7A6A54]">{hint}</div>}
  </label>
);

export const AuthPrimaryButton = ({ children, disabled, type = "submit", testId, onClick }) => (
  <button
    type={type}
    disabled={disabled}
    onClick={onClick}
    data-testid={testId}
    className="w-full py-3.5 rounded-[4px] font-medium text-[15px] transition-all disabled:opacity-50 disabled:cursor-not-allowed hover:brightness-105 active:scale-[0.995]"
    style={{
      background: "linear-gradient(180deg,#FFF6E5,#FFD9B5)",
      color: "#1F1B17",
      boxShadow: "0 6px 20px -6px rgba(255,214,150,0.35), inset 0 1px 0 rgba(255,255,255,0.4)",
    }}
  >
    {children}
  </button>
);

export const AuthErrorBanner = ({ error, testId }) =>
  error ? (
    <div
      data-testid={testId || "auth-error"}
      className="mb-5 px-4 py-3 border border-[#B43C3C]/40 bg-[#B43C3C]/10 rounded-[4px] text-sm text-[#F4D6B8]"
    >
      {error}
    </div>
  ) : null;

/**
 * Coerce FastAPI's `detail` payload into a string. Pydantic 422 errors
 * come back as `[{msg, loc, type}]`; rendering that array directly in
 * JSX crashes React with an "objects are not valid as a React child" error.
 */
export function formatAuthErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return (
      detail
        .map((e) => (e && typeof e.msg === "string" ? e.msg : null))
        .filter(Boolean)
        .join(" · ") || "Please check your input and try again."
    );
  if (detail && typeof detail.msg === "string") return detail.msg;
  return "Something went wrong. Please try again.";
}
